"use client";

import EventLink from "@components/links/eventLink";
import LoadingSpinner from "@components/loadingSpinner";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { fetcher } from "@utils/fetchers";
import { usePrevious } from "@utils/hooks";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { SyntheticEvent, useCallback, useEffect, useState } from "react";
import useSWR from "swr";
import { Filter, LandingPageLink } from "@features/search";
import { AnalyticsPage } from "@components/analytics";
import { HOST } from "@utils/constants";
import { useTranslations } from "next-intl";
import { Heading } from "@components/theming";
import { AnimatePresence, motion } from "framer-motion";

interface Event {
  Name: string;
  Url: string;
  Header: string;
  Region: string;
  Date: { Start: string | null; End: string | null; Range: string | null };
  Images: { BannerSmall: string; DefaultSmall: string; Portrait: string };
}

interface LandingPage extends Event {
  Footer: string;
  Images: {
    BannerSmall: string;
    DefaultSmall: string;
    Landingpage: string;
    Portrait: string;
  };
}

interface FacetItem {
  QKey: string;
  QValue: string;
  Label: string;
  Count: number;
}

interface Facet {
  Label: string;
  Items: FacetItem[];
}

interface Filter {
  Group: string;
  Key: string;
  Value: string;
  Label: string;
}

interface SearchResults {
  Events: Event[];
  LandingPages: LandingPage[];
  Facets: Facet[];
  Filters: Filter[];
  Paging: {
    PageCount: number;
    PageNumber: number;
  };
  Query: string;
}

export default function SearchPage() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const q = searchParams.get("q");
  const facetParams = searchParams.toString().split("&").slice(1).join("&");
  const { data: results, isLoading } = useSWR<SearchResults>(
    q ? `search/full/${q}?PageSize=${100}&${facetParams}` : null,
    fetcher
  );
  const previousResults = usePrevious(results).current;
  const resultsList = results ?? previousResults;
  const [confirmedQuery, setConfirmedQuery] = useState<string | null>(null);

  useEffect(() => {
    if (results) {
      setConfirmedQuery(q);
    }
  }, [results, q]);

  const isNewQueryLoading = isLoading && confirmedQuery !== q;
  const displayResults = isNewQueryLoading ? undefined : resultsList;
  const t = useTranslations("Search");

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(
        searchParams as unknown as URLSearchParams
      );
      if (value) {
        params.set(name, value);
      } else {
        params.delete(name);
      }

      const paramsString = params.toString();

      return paramsString ? `?${paramsString}` : "";
    },
    [searchParams]
  );

  function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const { search } = Object.fromEntries(new FormData(event.currentTarget));

    router.push(pathname + createQueryString("q", search as string));
  }

  function getRelativePath(url: string) {
    const rel_path = `/events/${url.split("/events/")[1]}`;

    return rel_path;
  }

  return (
    <div className="p-6">
      <Heading
        as="h2"
        size={{ initial: "7", sm: "8" }}
        weight="bold"
        shopsiteClasses="text-3xl sm:text-5xl font-black text-black"
      >
        {t("search")}
      </Heading>
      <div className="flex flex-col gap-8 mt-8">
        <motion.div layout className="flex flex-col gap-4">
          <motion.div
            layout="position"
            className="relative flex flex-col lg:flex-row gap-4 lg:items-center"
          >
            <form onSubmit={handleSubmit} className="flex items-center gap-2">
              <div className="relative flex items-center">
                <input
                  aria-label="search-input"
                  key={q}
                  name="search"
                  defaultValue={q ?? ""}
                  className="w-full lg:w-96 pl-7 pr-2 bg-white rounded-md border border-gray-300 px-2 py-1.5 text-black focus:outline-none focus:border-2 focus:py-[5px] focus:pl-[27px] focus:border-indigo-500"
                />
                <MagnifyingGlassIcon className="absolute left-2 text-black w-4 h-4" />
              </div>
              <button
                name="submit"
                type="submit"
                className="h-fit px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-300 text-white font-semibold transition-colors duration-200 ease-in-out"
              >
                {t("search")}
              </button>
            </form>
            <div className="flex py-2 lg:justify-end w-full overflow-x-auto lg:overflow-x-hidden">
              {resultsList ? (
                <Filter
                  values={resultsList.Filters}
                  options={resultsList?.Facets.filter(
                    (facet) => facet.Label !== "Type"
                  )}
                  onAdd={(option) => {
                    const prev = searchParams.get(option.QKey);
                    router.push(
                      pathname +
                        createQueryString(
                          option.QKey,
                          prev ? `${prev},${option.QValue}` : option.QValue
                        )
                    );
                  }}
                  onRemove={(option) => {
                    const prev = searchParams.get(option.QKey);
                    router.push(
                      pathname +
                        createQueryString(
                          option.QKey,
                          prev
                            ?.split(",")
                            .filter((value) => value !== option.QValue)
                            .join(",") ?? ""
                        )
                    );
                  }}
                />
              ) : (
                <></>
              )}
            </div>
          </motion.div>
          {resultsList ? (
            <motion.div layout className="flex gap-2 flex-wrap">
              <AnimatePresence initial={false} mode="popLayout">
                {resultsList.Filters.map((filter) => (
                  <motion.span
                    key={filter.Label}
                    layout
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    className="flex items-center gap-1 py-1 px-2 rounded-lg bg-indigo-100 text-indigo-800 text-sm"
                  >
                    {filter.Label}
                    <button
                      onClick={() => {
                        const prev = searchParams.get(filter.Key);
                        router.push(
                          pathname +
                            createQueryString(
                              filter.Key,
                              prev
                                ?.split(",")
                                .filter((value) => value !== filter.Value)
                                .join(",") ?? ""
                            )
                        );
                      }}
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </motion.span>
                ))}
              </AnimatePresence>
            </motion.div>
          ) : (
            <></>
          )}
        </motion.div>
        <div className="flex flex-col gap-8 overflow-hidden">
          {isNewQueryLoading ? (
            <motion.div
              layout="position"
              className="flex w-full justify-center items-center"
            >
              <LoadingSpinner size="48" />
            </motion.div>
          ) : (
            <></>
          )}
          {displayResults && displayResults.LandingPages.length > 0 ? (
            <motion.div layout="position" className="flex flex-col gap-4">
              <h3 className="text-2xl sm:text-3xl font-bold text-black">
                {t("eventseries")}
              </h3>
              <div className="relative grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                <AnimatePresence initial={false} mode="popLayout">
                  {(() => {
                    const landingPagesSignature =
                      displayResults.LandingPages.map((page) => page.Name).join(
                        ","
                      );

                    return displayResults.LandingPages.map((page) => (
                      <motion.div
                        key={`${page.Name}-${landingPagesSignature}`}
                        className="w-full"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        <LandingPageLink landingPage={page} />
                      </motion.div>
                    ));
                  })()}
                </AnimatePresence>
              </div>
            </motion.div>
          ) : (
            <></>
          )}
          {displayResults && displayResults.Events.length > 0 ? (
            <motion.div layout="position" className="flex flex-col gap-4">
              <h3 className="text-2xl sm:text-3xl font-bold text-black">
                {t("events")}
              </h3>
              <div className="relative grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-8">
                <AnimatePresence initial={false} mode="popLayout">
                  {(() => {
                    const eventsSignature = displayResults.Events.map(
                      (event) => `${event.Name}-${event.Region}`
                    ).join(",");

                    return displayResults.Events.map((event) => (
                      <motion.div
                        key={`${event.Name}-${event.Region}-${eventsSignature}`}
                        className="w-full"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        <EventLink
                          event={{
                            ...event,
                            Byline: event.Header,
                            Url: getRelativePath(event.Url),
                          }}
                          showEventRegions={true}
                        />
                      </motion.div>
                    ));
                  })()}
                </AnimatePresence>
              </div>
            </motion.div>
          ) : (
            <></>
          )}
          {!isNewQueryLoading &&
          (!displayResults?.Events || displayResults.Events.length === 0) &&
          (!displayResults?.LandingPages ||
            displayResults.LandingPages.length === 0) ? (
            <motion.p
              layout="position"
              className="text-lg font-semibold text-black"
            >
              {t("noresult")}
            </motion.p>
          ) : (
            <></>
          )}
        </div>
      </div>
      <AnalyticsPage
        properties={{
          path: "/search",
          title: "Search",
          url: `${HOST}/search`,
          search: `${q ? `?=${q}` : ""}`,
        }}
      />
    </div>
  );
}
